import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import "./Profile.css";
import { Row } from "react-bootstrap";
import instance, { AllCountryList, apiEncryptRequest, decryptData } from "../../Api";
import { useAuth } from "../../AuthContext";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../common/Loader";
import AppToast from "../../AppToast";
import { FaX } from "react-icons/fa6";
import TwoFactorSetup from "../TwoFactorSetup/TwoFactorSetup";

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_FILE_SIZE = 2 * 1024 * 1024;
const ERR_GENERIC   = "Oops! Something went wrong while fetching the data. Please try again later or contact support if the issue persists.";

const notEmpty = (v) => v !== null && v !== "" && v !== "null";

// ─── Shared sub-components ────────────────────────────────────────────────────
const VerifiedBadge = memo(() => (
  <img src="/images/verified_success.png" style={{ height: 34, width: 34 }} />
));

const VerifyBtn = memo(({ verified, onVerify, onEdit, width = 60 }) => (
  <button
    style={{ width, color: "black", marginRight: 5, borderRadius: 8 }}
    onClick={verified ? onEdit : onVerify}
  >
    {verified ? "Edit" : "Verify"}
  </button>
));

const VerifyField = memo(({ type = "text", placeholder, disabled, value, onChange, verified, onVerify, onEdit, extraClass = "" }) => (
  <div style={{ marginTop: 15, display: "flex", alignItems: "center", border: "2px solid white", borderRadius: 25, padding: 5 }}>
    <input
      type={type}
      className={`form-control white-placeholder ${extraClass}`}
      placeholder={placeholder}
      disabled={!!disabled}
      value={value ?? ""}
      style={{ color: "white", border: "none", outline: "none", background: "transparent", flex: 1, padding: 5 }}
      onChange={(e) => onChange(e.target.value)}
    />
    {verified && <VerifiedBadge />}
    <VerifyBtn verified={verified} onVerify={onVerify} onEdit={onEdit} />
  </div>
));

const ImageRow = memo(({ label, imageUrl, fileRef, onFileChange, onView, onRemove, onUpload, previewReady, isMobile }) => (
  <div style={{ marginTop: 15, display: "flex", alignItems: "center", border: "2px solid white", borderRadius: 25, padding: 5, height: 45, marginRight: isMobile ? 15 : 0 }}>
    {imageUrl ? (
      <>
        <a style={{ marginTop: 2, marginLeft: "5%", color: "white" }}>
          <button onClick={onView} style={{ borderRadius: 8 }}>View</button>{" "}{label}
        </a>
        <button onClick={onRemove} style={{ position: "absolute", right: 20, borderRadius: 8 }}>Remove</button>
      </>
    ) : (
      <>
        <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} style={{ color: "white", borderRadius: 10 }} />
        {previewReady && onUpload && (
          <button onClick={onUpload} style={{ position: "absolute", right: 20, borderRadius: 8 }}>Upload</button>
        )}
      </>
    )}
  </div>
));

const StateSelect = memo(({ label, value, states, onChange }) => (
  <select
    name="plan_time"
    style={{ marginTop: 10, borderRadius: 30, marginRight: 10, padding: "10px 15px", border: "2px solid rgb(255,255,255)", width: "100%" }}
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="Select">Select {label}</option>
    {states?.map((item, i) => <option key={i} value={item.name}>{item.name}</option>)}
  </select>
));

// ─── Main Component ───────────────────────────────────────────────────────────
const Profile = ({ isMobile }) => {
  const { smuser } = useAuth();

  const fileInputGSTRef        = useRef(null);
  const fileInputPANRef        = useRef(null);
  const fileInputAdharRef      = useRef(null);
  const fileInputCompanyPANRef = useRef(null);
  const fileInputCINRef        = useRef(null);
  const fileInputIdentityRef   = useRef(null);

  const [userData, setUserData]               = useState(null);
  const [vmData, setVmData]                   = useState(null);
  const [loading, setLoading]                 = useState(true);

  const [gstNum, setGstNum]           = useState(null);
  const [adharNum, setAdharNum]       = useState(null);
  const [panNum, setPanNum]           = useState(null);
  const [compPANNum, setCompPANNum]   = useState(null);
  const [cinNum, setCINNum]           = useState(null);
  const [identityNum, setIdentityNum] = useState(null);

  const [chkGSTVerify, setGSTVerified]           = useState(null);
  const [chkAdharVerify, setAdharVerified]       = useState(null);
  const [chkPANVerify, setPANVerified]           = useState(null);
  const [chkCompPANVerify, setCompPANVerified]   = useState(null);
  const [chkCINVerify, setCINVerified]           = useState(null);
  const [chkIdentiyVerify, setIdentityVerified]  = useState(null);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState]     = useState(null);
  const [userStates, setUserStates]           = useState(null);

  const [supportType, setSupportType] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [userName, setUserName]       = useState(null);
  const [userEmail, setUserEmail]     = useState(null);
  const [userMobile, setUserMobile]   = useState(null);

  const [showEditPopup, setShowEditPopup]     = useState(false);
  const [showAdharPopup, setShowAdharPopup]   = useState(false);
  const [adharRequestId, setAdharRequestId]   = useState(null);
  const [adharOTP, setAdharOTP]               = useState(null);
  const [showImagePopup, setShowImagePopup]   = useState(false);
  const [showPreviewImagePopup, setPreviewImagePopup] = useState(null);

  const [selectedGSTImage, setSelectedGSTImage]               = useState(null);
  const [selectedPANImage, setSelectedPANImage]               = useState(null);
  const [selectedAdharImage, setSelectedAdharImage]           = useState(null);
  const [selectedCompanyPANImage, setSelectedCompanyPANImage] = useState(null);
  const [selectedCINImage, setSelectedCINImage]               = useState(null);
  const [selectedIdentityImage, setIdentityImage]             = useState(null);
  const [selectedProfile, setSelectedProfile]                 = useState(null);

  const [previewGSTImage, setPreviewGSTImage]               = useState(null);
  const [previewPANImage, setPreviewPANImage]               = useState(null);
  const [previewAdharImage, setPreviewAdharImage]           = useState(null);
  const [previewCompanyPANImage, setPreviewCompanyPANImage] = useState(null);
  const [previewCINImage, setPreviewCINImage]               = useState(null);
  const [previewIdentityImage, setPreviewIdentityImage]     = useState(null);

  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />), [isMobile]);

  useEffect(() => { window.scrollTo(0, 0); GetMyDetails(); }, []);
  useEffect(() => { GetTemp("India"); }, []);

  const GetTemp = useCallback(async (country) => {
    setLoading(true);
    try {
      const res = await instance.post("https://countriesnow.space/api/v0.1/countries/states", { country });
      setUserStates(res.data.data.states);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const GetMyDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await instance.post("/editprofile", { user_id: smuser.id });
      const User = res.data.user;

      setSelectedCountry(User.country);
      setSelectedState(User.state_region);
      setUserData(User);

      if (notEmpty(User.gstno))          setGstNum(User.gstno);
      if (notEmpty(User.aadharno))       setAdharNum(User.aadharno);
      if (notEmpty(User.panno))          setPanNum(User.panno);
      if (notEmpty(User.driving_number)) setCompPANNum(User.driving_number);
      if (notEmpty(User.ciin_no))        setCINNum(User.ciin_no);
      if (notEmpty(User.identity_no))    setIdentityNum(User.identity_no);
      if (notEmpty(User.companyname))    setCompanyName(User.companyname);
      if (notEmpty(User.name))           setUserName(User.name);
      if (notEmpty(User.email))          setUserEmail(User.email);
      if (notEmpty(User.phone))          setUserMobile(User.phone);

      setSupportType(User.support);
      setGSTVerified(User.gst_verify === 1);
      setAdharVerified(User.aadhar_verify === 1);
      setPANVerified(User.pan_verify === 1);
      setCompPANVerified(User.driving_verify === 1);
      setCINVerified(User.ciib_verify === 1);
      setIdentityVerified(User.identity_verify === 1);

      setVmData(res.data.vm[0]);
    } catch { showToast(ERR_GENERIC); }
    setLoading(false);
  }, [smuser?.id, showToast]);

  const handleFileChange = useCallback((e, setFile, setPreview) => {
    setPreview(null);
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      showToast("Selected file size exceeds 2MB. Please choose a smaller file.");
      e.target.value = null;
      return;
    }
    setFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  }, [showToast]);

  const UploadImages = useCallback(async () => {
    if (!smuser?.id) { showToast(ERR_GENERIC); return; }
    setLoading(true);
    const formData = new FormData();
    formData.append("user_id", smuser.id);
    formData.append("name", smuser.name);
    formData.append("country", selectedCountry);
    formData.append("state", selectedState);
    formData.append("gstno", gstNum ?? "");
    formData.append("aadharno", adharNum ?? "");
    formData.append("panno", panNum ?? "");
    formData.append("driving_number", compPANNum ?? "");
    formData.append("ciin_no", cinNum ?? "");
    formData.append("gst_image", selectedGSTImage);
    formData.append("aadhar_image", selectedAdharImage);
    formData.append("pan_image", selectedPANImage);
    formData.append("driving_licence_img", selectedCompanyPANImage);
    formData.append("ciin_img", selectedCINImage);
    try {
      const res = await instance.post("/updateuserinfo", formData);
      if (res.status) {
        showToast("Image uploaded successfully");
        GetMyDetails();
        window.location.reload();
      }
    } catch (e) { showToast(String(e)); }
    setLoading(false);
  }, [smuser, selectedCountry, selectedState, gstNum, adharNum, panNum, compPANNum, cinNum, selectedGSTImage, selectedAdharImage, selectedPANImage, selectedCompanyPANImage, selectedCINImage, GetMyDetails, showToast]);

  const removeImageApi = useCallback(async (type) => {
    if (!smuser?.id) { showToast(ERR_GENERIC); return; }
    setLoading(true);
    try {
      const enc = await apiEncryptRequest({ user_id: smuser.id, type });
      const res = await instance.post("/removeimge", enc);
      const data = await decryptData(res.data);
      if (data.status) { showToast(data.message); GetMyDetails(); window.location.reload(); }
    } catch { showToast(ERR_GENERIC); }
    setLoading(false);
  }, [smuser?.id, GetMyDetails, showToast]);

  const handleButtonClick = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setSelectedProfile(file);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_id", smuser.id);
      setLoading(true);
      instance.post("/upload-image", formData)
        .then(() => GetMyDetails())
        .catch(() => showToast(ERR_GENERIC))
        .finally(() => setLoading(false));
    };
    input.click();
  }, [smuser?.id, GetMyDetails, showToast]);

  const updateUserProfile = useCallback(async () => {
    if (!userName || !userEmail || !userMobile) { showToast("User email and mobile both required!"); return; }
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: smuser.id, name: userName, email: userEmail, phone: userMobile, companyname: companyName });
      const res  = await instance.post("/updateuseprofile", enc);
      const data = await decryptData(res.data);
      setShowEditPopup(false);
      if (data.status) { showToast(data.message); GetMyDetails(); }
    } catch { setShowEditPopup(false); showToast(ERR_GENERIC); }
    setLoading(false);
  }, [smuser?.id, userName, userEmail, userMobile, companyName, GetMyDetails, showToast]);

  const apiVerify = useCallback(async (url, payload, onSuccess) => {
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest(payload);
      const res  = await instance.post(url, enc);
      const data = await decryptData(res.data);
      if (data.status) { showToast(data.message); if (onSuccess) onSuccess(data); GetMyDetails(); }
      else { showToast(data.message ?? ERR_GENERIC); }
    } catch { showToast(ERR_GENERIC); }
    setLoading(false);
  }, [showToast, GetMyDetails]);

  const gstVerify      = useCallback(() => { if (!gstNum) { showToast("GST Number required!"); return; } if (!companyName) { showToast("Company Name required!"); return; } apiVerify("/verifygst", { user_id: smuser.id, gstno: gstNum, gst_image: null, companyname: companyName }); }, [gstNum, companyName, smuser?.id, apiVerify, showToast]);
  const generateaadhar_otp = useCallback(() => { if (!adharNum) { showToast("Aadhar Number required!"); return; } apiVerify("/generateaadhar_otp", { user_id: smuser.id, aadharno: adharNum, aadhar_image: null }, (data) => { setAdharRequestId(data.request_id); setShowAdharPopup(true); }); }, [adharNum, smuser?.id, apiVerify, showToast]);

  const adharVerify = useCallback(async () => {
    if (!adharOTP) { showToast("OTP required!"); return; }
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: smuser.id, request_id: adharRequestId, otp: adharOTP });
      const res  = await instance.post("/verifyaadhar", enc);
      const data = await decryptData(res.data);
      if (data.status) { showToast(data.message); GetMyDetails(); }
      setShowAdharPopup(false);
    } catch { setShowAdharPopup(false); }
    setLoading(false);
  }, [adharOTP, smuser?.id, adharRequestId, GetMyDetails, showToast]);

  const panVerify     = useCallback(() => { if (!panNum) { showToast("PAN Number required!"); return; } apiVerify("/verifypan", { user_id: smuser.id, pan_no: panNum, pan_image: null }); }, [panNum, smuser?.id, apiVerify, showToast]);
  const compPanVerify = useCallback(() => { if (!compPANNum) { showToast("Company PAN Number required!"); return; } if (!companyName) { showToast("Company Name required!"); return; } apiVerify("/driving-license", { user_id: smuser.id, driving_number: compPANNum, driving_licence_img: null, companyname: companyName }); }, [compPANNum, companyName, smuser?.id, apiVerify, showToast]);
  const cinVerify     = useCallback(() => { if (!cinNum) { showToast("CIN Number required!"); return; } if (!companyName) { showToast("Company Name required!"); return; } apiVerify("/verifyciin", { user_id: smuser.id, ciin_no: cinNum, ciin_img: null, companyname: companyName }); }, [cinNum, companyName, smuser?.id, apiVerify, showToast]);

  const IdentityVerify = useCallback(async () => {
    if (!identityNum)       { showToast("Identity Number required!"); return; }
    if (!selectedIdentityImage) { showToast("Identity Image required!"); return; }
    setLoading(true);
    const formData = new FormData();
    formData.append("user_id", smuser.id);
    formData.append("identity_no", identityNum);
    formData.append("identity_image", selectedIdentityImage);
    try {
      const res  = await instance.post("/verifyidentity", formData);
      const data = await decryptData(res.data);
      if (data.status) { if (data.url) window.open(data.url, "_blank"); showToast("We will verify your identity shortly."); GetMyDetails(); }
    } catch { showToast(ERR_GENERIC); }
    setLoading(false);
  }, [identityNum, selectedIdentityImage, smuser?.id, GetMyDetails, showToast]);

  const isIndia      = selectedCountry === "India";
  const noneVerified = !chkGSTVerify && !chkAdharVerify && !chkPANVerify && !chkCompPANVerify && !chkCINVerify;

  const IndiaFields = useMemo(() => (
    <>
      <VerifyField placeholder="GST Number"      value={gstNum}     onChange={setGstNum}     verified={chkGSTVerify}     onVerify={gstVerify}          onEdit={() => setGSTVerified(false)} />
      {noneVerified && isIndia && <div className="arrow"><span /><span /><span /></div>}
      <VerifyField placeholder="Aadhar Number"   value={adharNum}   onChange={setAdharNum}   verified={chkAdharVerify}   onVerify={generateaadhar_otp} onEdit={() => setAdharVerified(false)} type="Number" extraClass="no-arrows"
        extraChildren={<button className={noneVerified ? "loader" : undefined} style={{ height: 30, width: 60, color: "black", marginRight: 5, borderRadius: 8 }} onClick={chkAdharVerify ? () => setAdharVerified(false) : generateaadhar_otp}>{chkAdharVerify ? "Edit" : "Verify"}</button>} />
      <VerifyField placeholder="PAN Number"      value={panNum}     onChange={setPanNum}     verified={chkPANVerify}     onVerify={panVerify}          onEdit={() => setPANVerified(false)} />
      <VerifyField placeholder="Company PAN No." value={compPANNum} onChange={setCompPANNum} verified={chkCompPANVerify} onVerify={compPanVerify}      onEdit={() => setCompPANVerified(false)} />
      <VerifyField placeholder="CIN No."         value={cinNum}     onChange={setCINNum}     verified={chkCINVerify}     onVerify={cinVerify}          onEdit={() => setCINVerified(false)} />
    </>
  ), [gstNum, adharNum, panNum, compPANNum, cinNum, chkGSTVerify, chkAdharVerify, chkPANVerify, chkCompPANVerify, chkCINVerify, noneVerified, isIndia, gstVerify, generateaadhar_otp, panVerify, compPanVerify, cinVerify]);

  const InternationalField = useMemo(() => (
    <VerifyField placeholder="Identity Number" value={identityNum} onChange={setIdentityNum} verified={chkIdentiyVerify} onVerify={IdentityVerify} onEdit={() => setIdentityVerified(false)} />
  ), [identityNum, chkIdentiyVerify, IdentityVerify]);

  const makeImgRow = useCallback((label, imageKey, fileRef, onFileChange, previewReady, removeKey) => (
    <ImageRow key={label} label={label} imageUrl={userData?.[imageKey] ?? null} fileRef={fileRef} onFileChange={onFileChange}
      onView={() => { setPreviewImagePopup(userData && `${userData[imageKey]}`); setShowImagePopup(true); }}
      onRemove={() => removeImageApi(removeKey)} onUpload={UploadImages} previewReady={previewReady} isMobile={isMobile} />
  ), [userData, removeImageApi, UploadImages, isMobile]);

  const IndiaImageRows = useMemo(() => [
    makeImgRow("GST or VAT Image",   "gst_image",           fileInputGSTRef,        (e) => handleFileChange(e, setSelectedGSTImage, setPreviewGSTImage),               !!previewGSTImage,        "gst"),
    makeImgRow("Adhar Image",        "aadhar_image",        fileInputAdharRef,      (e) => handleFileChange(e, setSelectedAdharImage, setPreviewAdharImage),           !!previewAdharImage,      "aadhar"),
    makeImgRow("PAN Card Image",     "pan_image",           fileInputPANRef,        (e) => handleFileChange(e, setSelectedPANImage, setPreviewPANImage),               !!previewPANImage,        "pan"),
    makeImgRow("Company PAN image",  "driving_licence_img", fileInputCompanyPANRef, (e) => handleFileChange(e, setSelectedCompanyPANImage, setPreviewCompanyPANImage), !!previewCompanyPANImage, "driver"),
    makeImgRow("CIN No Image",       "ciin_img",            fileInputCINRef,        (e) => handleFileChange(e, setSelectedCINImage, setPreviewCINImage),               !!previewCINImage,        "ciin"),
  ], [makeImgRow, previewGSTImage, previewAdharImage, previewPANImage, previewCompanyPANImage, previewCINImage, handleFileChange]);

  const IdentityImageRow = useMemo(() => (
    <ImageRow label="Identity Image" imageUrl={userData?.identity_image ?? null} fileRef={fileInputIdentityRef}
      onFileChange={(e) => handleFileChange(e, setIdentityImage, setPreviewIdentityImage)}
      onView={() => { setPreviewImagePopup(userData && `${userData.identity_image}`); setShowImagePopup(true); }}
      onRemove={() => removeImageApi("identity")} onUpload={null} previewReady={!!previewIdentityImage} isMobile={isMobile} />
  ), [userData, previewIdentityImage, handleFileChange, removeImageApi, isMobile]);

  const updateBtnStyle  = { color: "white", width: "10rem", height: 50, fontWeight: "bold", backgroundColor: "#035189", borderRadius: 25, border: "2px solid #ffff", outline: "2px solid #035189", marginTop: 110 };
  const closeBtnStyle   = { zIndex: 9, position: "absolute", backgroundColor: "transparent", border: "none", right: 0 };
  const popupInputStyle = { top: "50px", right: "0px", marginTop: isMobile ? "60px" : "30px" };
  const inputStyle      = (w) => ({ borderRadius: 10, width: w, marginTop: -2 });

  // ── 2FA stat card — live status from userData ─────────────────────────────
  const twoFaActive = userData?.totp_enabled === 1;

  return (
    <div style={{ backgroundImage: "url(./main-bg.jpg)" }}>

      {/* Edit profile popup */}
      {showEditPopup && userData && (
        <div style={{ display: "grid", justifyItems: "center" }}>
          <div style={{ backdropFilter: "blur(25px)", borderRadius: 12, border: "2px solid #e97730", top: "20%", position: "absolute", zIndex: 999999999999, width: isMobile ? "80%" : "35%", height: "35rem" }}>
            <div style={{ display: "grid", justifyItems: "center" }}>
              <button style={closeBtnStyle} onClick={() => setShowEditPopup(false)}>
                <FaX style={{ marginTop: 5, color: "#e97730", fontSize: 19 }} />
              </button>
              {[
                { placeholder: "Company Name",  default: userData.companyname, setter: setCompanyName },
                { placeholder: "Name",          default: userData.name,        setter: setUserName },
                { placeholder: "Email Address", default: userData.email,       setter: setUserEmail },
                { placeholder: "Mobile Number", default: userData.phone,       setter: setUserMobile, type: "number" },
              ].map(({ placeholder, default: def, setter, type = "text" }) => (
                <div key={placeholder} className="popup-input-container" style={popupInputStyle}>
                  <input defaultValue={def} type={type} className="input-signup" placeholder={placeholder}
                    style={inputStyle(isMobile ? "15rem" : "25rem")} onChange={(e) => setter(e.target.value)} />
                </div>
              ))}
              <button style={updateBtnStyle} onClick={updateUserProfile}>Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Aadhar OTP popup */}
      {showAdharPopup && (
        <div style={{ display: "grid", justifyItems: "center" }}>
          <div style={{ backdropFilter: "blur(25px)", borderRadius: 12, border: "2px solid #e97730", top: "30%", position: "absolute", zIndex: 999999999999, width: isMobile ? "80%" : "35%", height: "20rem" }}>
            <div style={{ display: "grid", justifyItems: "center" }}>
              <button style={closeBtnStyle} onClick={() => setShowAdharPopup(false)}>
                <FaX style={{ marginTop: 5, color: "#e97730", fontSize: 19 }} />
              </button>
              <div className="popup-input-container" style={popupInputStyle}>
                <input type="text" className="input-signup" placeholder="Enter OTP"
                  style={inputStyle(isMobile ? "15rem" : "25rem")} onChange={(e) => setAdharOTP(e.target.value)} />
              </div>
              <button style={updateBtnStyle} onClick={adharVerify}>Verify OTP</button>
            </div>
          </div>
        </div>
      )}

      {/* Image preview popup */}
      {showImagePopup && (
        <div style={{ display: "grid", justifyItems: "center" }}>
          <div style={{ backgroundColor: "white", borderRadius: 12, border: "2px solid #e97730", top: "30%", position: "absolute", zIndex: 999999999999, width: isMobile ? "80%" : "30%", height: isMobile ? "40%" : "30rem" }}>
            <div style={{ display: "grid", justifyItems: "center" }}>
              <button style={{ ...closeBtnStyle, backgroundColor: "white" }} onClick={() => setShowImagePopup(false)}>
                <FaX style={{ marginTop: 5, color: "#e97730", fontSize: 19 }} />
              </button>
              {showPreviewImagePopup && (
                <img src={showPreviewImagePopup} style={{ width: "70%", height: "70%", borderRadius: 8, marginTop: 55 }} />
              )}
            </div>
          </div>
        </div>
      )}

      {isMobile ? (
        /* ── MOBILE ──────────────────────────────────────────────────────────── */
        <div style={{ marginTop: "4rem", display: "grid", justifyItems: "center", justifyContent: "center" }}>
          <div className="profile-image-cont">
            <div className="profile-image-outer">
              <div className="profile-image-inner" style={{ backgroundColor: "#e97730" }}>
                <img src={userData?.image ?? "/admin/images/admin/common/admin-icon.png"} alt="Profile" style={{ padding: userData?.image ? 10 : 15 }} />
              </div>
            </div>
            <div className="profile-edit-badge" onClick={handleButtonClick}>
              <img src="/admin/images/admin/13-Profile/Pen.png" style={{ paddingRight: 5 }} />Edit Photo
            </div>
          </div>

          <div>
            <div style={{ marginTop: "-4rem", backgroundImage: `url("/images/blue-box-bg.svg")`, backgroundSize: "cover", top: "5rem", paddingRight: 5, paddingBottom: 50, width: "90%", marginLeft: 20, position: "relative", backgroundColor: "#07528b", borderRadius: 12, textAlign: "center" }}>
              <div style={{ position: "relative", display: "flex", flexWrap: "wrap", zIndex: 1 }}>
                <div style={{ padding: "0px 10px" }}>
                  <select name="plan_time" style={{ marginTop: 17, borderRadius: 30, marginRight: 15, padding: "10px 15px", border: "2px solid rgb(255,255,255)", width: "22rem" }}
                    onChange={(e) => { setSelectedCountry(e.target.value); GetTemp(e.target.value); }}>
                    <option value="Select">Country</option>
                    {AllCountryList?.map((item, i) => <option key={i} value={item.value}>{item.name}</option>)}
                  </select>
                  <StateSelect label={isIndia ? "State" : "Province"} value={selectedState} states={userStates} onChange={setSelectedState} />
                  {isIndia ? IndiaFields : InternationalField}
                  {isIndia ? IndiaImageRows : IdentityImageRow}

                  {/* ── 2FA Setup (mobile) ── */}
                  <TwoFactorSetup isMobile={isMobile} userData={userData} onStatusChange={GetMyDetails} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ── WEB ─────────────────────────────────────────────────────────────── */
        <div style={{ display: "grid", justifyItems: "center", justifyContent: "center" }}>
          <Row>
            <div className="col-md-3" style={{ marginTop: 120, paddingLeft: 80 }}>
              <div className="profile-image-cont" style={{ paddingLeft: 80 }}>
                <div className="profile-image-outer" style={{ backgroundColor: "white" }}>
                  <div className="profile-image-inner">
                    <img src={userData?.image ?? "/admin/images/admin/common/admin-icon.png"} alt="Profile" style={{ padding: userData?.image ? 10 : 15, color: "white" }} />
                  </div>
                </div>
                <div className="profile-edit-badge" onClick={handleButtonClick}>
                  <img src="/admin/images/admin/13-Profile/Pen.png" style={{ paddingRight: 5 }} />Edit Photo
                </div>
              </div>
            </div>

            <div className="col-md-9">
              {/* ── Stat cards ── */}
              <div className="details-profile-edit" style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: 10, paddingRight: 20 }}>
                {userData && (
                  <>
                    {[
                      { icon: "/admin/images/admin/13-Profile/user-white.png", title: "Company Name", value: userData.companyname || "N/A" },
                      { icon: "/admin/images/admin/13-Profile/user-white.png", title: "Name",         value: userData.name },
                      { icon: "/admin/images/admin/13-Profile/email.png",      title: "Email",        value: userData.email },
                      ...(userData.phone ? [{ icon: "/admin/images/admin/13-Profile/phone.png", title: "Mobile No.", value: userData.phone }] : []),
                    ].map(({ icon, title, value }) => (
                      <div key={title} className="stat" style={{ width: "13rem" }}>
                        <div className="machine-icon-edit-profile"><img src={icon} /></div>
                        <div className="machine-title theme-bg-orange">{title}</div>
                        <div className="mid-portion" />
                        <div className="machine-subtitle theme-bg-blue" style={{ textAlign: "left", width: 190 }}>{value}</div>
                        <div className="profile-edit-badge-details" onClick={() => setShowEditPopup(true)}>
                          <img className="edit-iconimage" src="/admin/images/admin/13-Profile/Pen.png" style={{ paddingRight: 1 }} />
                        </div>
                      </div>
                    ))}

                    <div className="stat" style={{ width: "13rem" }}>
                      <div className="machine-icon-edit-profile"><img src="/admin/images/admin/13-Profile/server_icon.png" /></div>
                      <div className="machine-title theme-bg-orange">Total Machine</div>
                      <div className="mid-portion" />
                      <div className="machine-subtitle theme-bg-blue" style={{ width: 190 }}>{vmData?.total}</div>
                    </div>

                    <div className="stat" style={{ width: "11rem" }}>
                      <div className="machine-icon-edit-profile"><img src="/admin/images/admin/13-Profile/support.png" /></div>
                      <div className="machine-title theme-bg-orange">Support</div>
                      <div className="mid-portion" />
                      <div className="machine-subtitle theme-bg-blue" style={{ width: 170 }}>{supportType}</div>
                    </div>

                    {/* ── 2FA stat card — live status ── */}
                    <div className="stat" style={{ width: "11rem" }}>
                      <div className="machine-icon-edit-profile">
                        <img src="/images/admin/06-View-Stats/switch.svg" />
                      </div>
                      <div
                        className="machine-title"
                        style={{ backgroundColor: twoFaActive ? "green" : "#e97730" }}
                      >
                        2FA {twoFaActive ? "ON" : "OFF"}
                      </div>
                      <div className="mid-portion" />
                      <div
                        className="machine-subtitle theme-bg-blue"
                        style={{ width: 170, fontSize: 12, fontWeight: 700 }}
                      >
                        {twoFaActive ? "🔐 Protected" : "⚠️ Not set up"}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* ── Verification form + 2FA Setup ── */}
              <div style={{ backgroundImage: `url("/images/blue-box-bg.svg")`, backgroundSize: "cover", top: "5rem", width: "100%", padding: "50px 50px", position: "relative", backgroundColor: "#07528b", borderRadius: 12 }}>
                <div style={{ position: "relative", display: "flex", flexWrap: "wrap", zIndex: 1 }}>
                  {/* Left column */}
                  <div style={{ width: "50%", padding: "0px 10px" }}>
                    <select name="plan_time" style={{ marginTop: 13, borderRadius: 30, marginRight: 10, padding: "10px 15px", border: "2px solid rgb(255,255,255)", width: "100%" }}
                      value={selectedCountry}
                      onChange={(e) => { setSelectedCountry(e.target.value); GetTemp(e.target.value); setIdentityImage(null); }}>
                      <option value="Select">Select Country</option>
                      {AllCountryList?.map((item, i) => <option key={i} value={item.name}>{item.name}</option>)}
                    </select>
                    {isIndia ? IndiaFields : InternationalField}
                  </div>

                  {/* Right column */}
                  <div style={{ marginTop: 5, width: "50%", padding: "0px 10px" }}>
                    <StateSelect label={isIndia ? "State" : "Province"} value={selectedState} states={userStates} onChange={setSelectedState} />
                    {isIndia ? IndiaImageRows : IdentityImageRow}
                  </div>
                </div>

                {/* ── 2FA Setup — full width below verification columns ── */}
                <div style={{ marginTop: 30 }}>
                  <TwoFactorSetup isMobile={isMobile} userData={userData} onStatusChange={GetMyDetails} />
                </div>
              </div>

            </div>
          </Row>
        </div>
      )}

      <div className="apptoast-align">
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>

      {loading && (
        <div className="loading-overlay" style={{ position: "absolute", zIndex: 9999999999 }}>
          <Loader isLoading={loading} />
        </div>
      )}
    </div>
  );
};

export default Profile;